import { useCallback } from 'react';

import { useStoreLocatorPerformanceImprovements } from 'experiments/use-store-locator-performance-improvements';
import {
  useGetNearbyRestaurantsLazyQuery,
  useGetRestaurantsLazyQuery,
} from 'generated/graphql-gateway';
import { FilterRestaurantType, IRestaurantsInput, ServiceMode } from 'generated/rbi-graphql';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';

interface IFetchRestaurantsOptions {
  allowFromCache: boolean;
}

const useCachedGetRestaurantsLazyQuery = withLazySimpleCache(useGetRestaurantsLazyQuery);

const useCachedGetNearbyRestaurantsLazyQuery = withLazySimpleCache(
  useGetNearbyRestaurantsLazyQuery
);

export const useRestaurants = (filter: FilterRestaurantType) => {
  const [fetchRestaurants, { data, loading, error }] = useCachedGetRestaurantsLazyQuery();

  const fetch = useCallback(
    (
      input: IRestaurantsInput = {},
      options: IFetchRestaurantsOptions = { allowFromCache: true }
    ) => {
      fetchRestaurants({
        allowFromCache: options.allowFromCache,
        variables: { input: { filter, ...input } },
      });
    },
    [fetchRestaurants, filter]
  );

  return { fetch, data, loading, error };
};

export const useNearbyRestaurants = (allowPerfExperiment?: boolean) => {
  // TODO backend still needs to support US / CA caches (only US supported right now) and we can then remove this
  const isPerfExperimentActive = useStoreLocatorPerformanceImprovements(allowPerfExperiment);

  const [
    fetchRestaurantsOld,
    { data: dataOld, loading: loadingOld, error: errorOld },
  ] = useCachedGetRestaurantsLazyQuery();
  const [
    fetchRestaurantsNew,
    { data: dataNew, loading: loadingNew, error: errorNew },
  ] = useCachedGetNearbyRestaurantsLazyQuery();

  const fetch = useCallback(
    (
      input: IRestaurantsInput = {},
      options: IFetchRestaurantsOptions = { allowFromCache: true }
    ) => {
      return isPerfExperimentActive
        ? fetchRestaurantsNew({
            allowFromCache: options.allowFromCache,
            variables: {
              input: {
                pagination: {
                  after: input.after,
                  before: input.before,
                  first: input.first,
                  last: input.last,
                },
                radiusStrictMode: !!input.radiusStrictMode,
                serviceModes: input?.serviceModes?.map(x => x as ServiceMode) ?? undefined,
                coordinates: {
                  searchRadius: input?.coordinates?.searchRadius ?? undefined,
                  userLat: input.coordinates?.userLat,
                  userLng: input.coordinates?.userLng,
                },
              },
            },
          }).then(x => x.data?.restaurantsV2?.nearby?.nodes)
        : fetchRestaurantsOld({
            allowFromCache: options.allowFromCache,
            variables: { input: { filter: FilterRestaurantType.NEARBY, ...input } },
          }).then(x => x?.data?.restaurants?.nodes);
    },
    [fetchRestaurantsOld, fetchRestaurantsNew, isPerfExperimentActive]
  );

  return {
    fetch,
    stores: isPerfExperimentActive
      ? dataNew?.restaurantsV2?.nearby?.nodes
      : dataOld?.restaurants?.nodes,
    loading: isPerfExperimentActive ? loadingNew : loadingOld,
    error: isPerfExperimentActive ? errorNew : errorOld,
  };
};
